import { useState } from 'react';
import { flushSync } from 'react-dom';

import {
    autoPlacement,
    autoUpdate,
    offset,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useTransitionStyles,
} from '@floating-ui/react';

type useFloatingDropdownConfig = {
    defaultOpen?: boolean;
};

export function useFloatingDropdown({ defaultOpen = false }: useFloatingDropdownConfig = {}) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [maxHeight, setMaxHeight] = useState<number | null>(null);
    const { refs, floatingStyles, context } = useFloating({
        middleware: [
            autoPlacement({
                allowedPlacements: ['bottom-start', 'top-start', 'bottom-end', 'top-end'],
            }),
            offset(2),
            size({
                apply({ availableHeight }) {
                    flushSync(() => setMaxHeight(availableHeight - 20));
                },
            }),
        ],
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
    });
    const click = useClick(context, { toggle: true });
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);
    const { styles, isMounted } = useTransitionStyles(context);

    return {
        isOpen,
        setIsOpen,
        refs,
        floatingStyles,
        context,
        getReferenceProps,
        getFloatingProps,
        styles,
        isMounted,
        maxHeight,
    };
}
